import { spawn } from "node:child_process";
import { closeSync, openSync, readFileSync } from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { readBootstrapState } from "./bootstrap-state.js";
import { readStatusBlob } from "./bootstrap-status.js";
import { getFoundryToken } from "./auth.js";
import { LocalCliError } from "./errors.js";
import { colors } from "./output.js";
import {
  findOnboardingProfile, renderCompanyProfile,
  DEFAULT_MEMORY_INDEX_HEADER, COMPANY_PROFILE_PATH, renderFounderRecord,
  FOUNDER_RECORD_PATH, type CanonicalOnboardingBlock,
} from "./onboarding-profile.js";
import { mintInstallationTokenFromPem, readRepoFileViaApp, commitFilesViaApp } from "./github-app-repo.js";
import { getSignedInUserIdentity } from "./founder-identity.js";

export interface GithubAppCreds { appId: string; slug: string; org: string; installationId: string; pemPath: string }

export function readGithubAppCreds(credsPath?: string): GithubAppCreds | null {
  const p = credsPath ?? path.join(os.homedir(), ".m8t", "github-app.json");
  // malformed or absent → null (best-effort seed; `launch` already validated these creds for a real install).
  try { return JSON.parse(readFileSync(p, "utf8")) as GithubAppCreds; } catch { return null; }
}

export interface SeedContext { endpoint: string; brainRepos: string[]; appCreds: GithubAppCreds; subscriptionId?: string }

export async function resolveSeedContext(opts: {
  endpointOverride?: string; brainOverride?: string; credsPath?: string;
  appCredsOverride?: GithubAppCreds; subscriptionId?: string;
}): Promise<SeedContext | null> {
  const appCreds = opts.appCredsOverride ?? readGithubAppCreds(opts.credsPath);
  if (!appCreds) return null; // no brains were deployed
  let endpoint = opts.endpointOverride;
  let subscriptionId = opts.subscriptionId;
  if (!endpoint) {
    const state = await readBootstrapState();
    if (!state) {
      throw new LocalCliError({ code: "BOOTSTRAP_NO_STATE", message: "No bootstrap state found.", hint: "Run 'm8t bootstrap launch' first, or pass --endpoint." });
    }
    subscriptionId ??= state.subscriptionId;
    const doc = await readStatusBlob({ saName: state.statusSaName, resourceGroup: state.resourceGroup, subscriptionId: state.subscriptionId });
    endpoint = doc.result?.foundryEndpoint;
    if (!endpoint) {
      throw new LocalCliError({ code: "SEED_NO_ENDPOINT", message: "Could not resolve the Foundry endpoint from the install status.", hint: "Pass --endpoint <foundryEndpoint>." });
    }
  }
  const brainRepos = opts.brainOverride
    ? [opts.brainOverride]
    : [`${appCreds.org}/stacey-brain`, `${appCreds.org}/azzy-brain`];
  return { endpoint, brainRepos, appCreds, subscriptionId };
}

export interface ApplyProfileToBrainArgs {
  block: CanonicalOnboardingBlock; brainRepo: string; branch: string; appCreds: GithubAppCreds;
  subscriptionId?: string; azIdentity?: { name: string; email: string };
  now?: string; fetchImpl?: typeof fetch;
}

const MEMORY_INDEX_PATH = "memory/MEMORY.md";

/** Ignore generated timestamps only when they are fields in the leading YAML frontmatter. */
function normalizeFrontmatterTimestamps(markdown: string): string {
  const lines = markdown.split("\n");
  if (lines[0]?.replace(/\r$/, "") !== "---") return markdown;
  const end = lines.findIndex((line, index) => index > 0 && line.replace(/\r$/, "") === "---");
  if (end < 0) return markdown;
  return lines.map((line, index) => {
    if (index <= 0 || index >= end) return line;
    return /^(created|updated):/.test(line) ? line.replace(/^(created|updated):.*$/, "$1: <generated>") : line;
  }).join("\n");
}

function seededDocumentMatches(actual: string | null, expected: string): boolean {
  return actual != null && normalizeFrontmatterTimestamps(actual) === normalizeFrontmatterTimestamps(expected);
}

/** Replace every prior entry for targetPath with exactly one current entry. */
function upsertMemoryIndexOnce(existing: string, line: string, targetPath: string): string {
  const lines = existing.split("\n");
  const kept: string[] = [];
  let insertionIndex: number | undefined;
  for (const existingLine of lines) {
    if (existingLine.includes(`\`${targetPath}\``)) {
      insertionIndex ??= kept.length;
    } else {
      kept.push(existingLine);
    }
  }

  if (insertionIndex === undefined) {
    const firstMemoryItem = kept.findIndex((existingLine) => /^-\s+`memory\//.test(existingLine));
    if (firstMemoryItem >= 0) {
      insertionIndex = firstMemoryItem;
    } else {
      const base = kept.join("\n").replace(/\s*$/, "\n\n");
      return `${base}${line}\n`;
    }
  }
  kept.splice(insertionIndex, 0, line);
  return kept.join("\n");
}

export async function applyProfileToBrain(args: ApplyProfileToBrainArgs): Promise<void> {
  const token = await mintInstallationTokenFromPem({
    appId: args.appCreds.appId,
    privateKeyPem: readFileSync(args.appCreds.pemPath, "utf8"),
    installationId: args.appCreds.installationId,
    fetchImpl: args.fetchImpl,
  });
  const { profileMd, memoryIndexLine: companyLine } = renderCompanyProfile(args.block, args.now);
  const { founderMd, memoryIndexLine: founderLine } = renderFounderRecord(
    args.block, { subscriptionId: args.subscriptionId, azIdentity: args.azIdentity }, args.now);

  const read = (p: string) => readRepoFileViaApp({
    token,
    repo: args.brainRepo,
    path: p,
    ref: args.branch,
    fetchImpl: args.fetchImpl,
  });
  const existingProfile = await read(COMPANY_PROFILE_PATH);
  const existingFounder = await read(FOUNDER_RECORD_PATH);
  const existingIndex = (await read(MEMORY_INDEX_PATH)) ?? DEFAULT_MEMORY_INDEX_HEADER;

  let nextIndex = upsertMemoryIndexOnce(existingIndex, companyLine, COMPANY_PROFILE_PATH);
  nextIndex = upsertMemoryIndexOnce(nextIndex, founderLine, FOUNDER_RECORD_PATH);

  const files = [
    ...(!seededDocumentMatches(existingProfile, profileMd)
      ? [{ path: COMPANY_PROFILE_PATH, content: profileMd }]
      : []),
    ...(!seededDocumentMatches(existingFounder, founderMd)
      ? [{ path: FOUNDER_RECORD_PATH, content: founderMd }]
      : []),
    ...(nextIndex !== existingIndex
      ? [{ path: MEMORY_INDEX_PATH, content: nextIndex }]
      : []),
  ];

  if (files.length > 0) {
    await commitFilesViaApp({
      token, repo: args.brainRepo, branch: args.branch,
      message: "seed(brain): founder + company profile from onboarding",
      files,
      fetchImpl: args.fetchImpl,
    });

    const [verifiedProfile, verifiedFounder, verifiedIndex] = await Promise.all([
      read(COMPANY_PROFILE_PATH),
      read(FOUNDER_RECORD_PATH),
      read(MEMORY_INDEX_PATH),
    ]);
    const mismatches = [
      ...(!seededDocumentMatches(verifiedProfile, profileMd) ? [COMPANY_PROFILE_PATH] : []),
      ...(!seededDocumentMatches(verifiedFounder, founderMd) ? [FOUNDER_RECORD_PATH] : []),
      ...(verifiedIndex !== nextIndex ? [MEMORY_INDEX_PATH] : []),
    ];
    if (mismatches.length > 0) {
      throw new LocalCliError({
        code: "SEED_PROFILE_READBACK_FAILED",
        message: `Profile write could not be verified in ${args.brainRepo}: ${mismatches.join(", ")}.`,
        hint: "Re-run 'm8t bootstrap seed-profile'; verified files will be left unchanged.",
      });
    }
  }
}

/** Attempt every brain independently, but resolve only when every brain verifies all three files. */
export async function applyProfileToBrains(
  args: Omit<ApplyProfileToBrainArgs, "brainRepo"> & { brainRepos: string[] },
): Promise<void> {
  const brainRepos = [...new Set(args.brainRepos)];
  if (brainRepos.length === 0) {
    throw new LocalCliError({
      code: "SEED_PROFILE_INCOMPLETE",
      message: "Profile seeding is incomplete: no brain repositories were resolved.",
      hint: "Re-run 'm8t bootstrap seed-profile' after the Stacey and Azzy brains are provisioned.",
    });
  }
  const settled = await Promise.allSettled(brainRepos.map(async (brainRepo) => {
    await applyProfileToBrain({ ...args, brainRepo });
    return brainRepo;
  }));
  const verified: string[] = [];
  const failed: string[] = [];
  const causes: unknown[] = [];
  settled.forEach((result, index) => {
    const brainRepo = brainRepos[index];
    if (result.status === "fulfilled") {
      verified.push(brainRepo);
    } else {
      failed.push(brainRepo);
      causes.push(result.reason);
    }
  });
  if (failed.length > 0 || verified.length !== brainRepos.length) {
    throw new LocalCliError({
      code: "SEED_PROFILE_INCOMPLETE",
      message: `Profile seeding is incomplete: verified ${verified.join(", ") || "none"}; failed ${failed.join(", ") || "none"}.`,
      hint: "Re-run 'm8t bootstrap seed-profile'; verified brain files will be skipped and missing files repaired.",
      cause: new AggregateError(causes, "One or more brain profile writes failed verification."),
    });
  }
}

/** Spawn a detached `m8t bootstrap seed-profile --watch` that survives this process. */
export function spawnDetachedSeedWatch(): void {
  const logPath = path.join(os.homedir(), ".m8t", "seed-profile.log");
  const fd = openSync(logPath, "a");
  try {
    const child = spawn(process.execPath, [process.argv[1] ?? "", "bootstrap", "seed-profile", "--watch"], {
      detached: true, stdio: ["ignore", fd, fd],
    });
    child.unref();
  } finally {
    closeSync(fd);
  }
}

/**
 * Reactive seed once the install reports `done`: seed inline if the founder has
 * already finished the intake; else leave a detached background watcher that
 * seeds when they do; else (they skipped the intake entirely) no-op.
 *
 * Called from bootstrap-finalize, which `bootstrap status --watch` drives. Named
 * for the moment rather than for the command that used to own it — the command
 * (`bootstrap finish`) is gone; the moment is not.
 */
export async function reactiveSeedOnInstallComplete(args: {
  endpoint?: string; stdout: (s: string) => void;
  getFoundryTokenImpl?: () => Promise<string>; spawnWatch?: () => void;
  fetchImpl?: typeof fetch; appCredsOverride?: GithubAppCreds;
  subscriptionId?: string; azIdentity?: { name: string; email: string };
  getSignedInUserIdentityImpl?: () => Promise<{ name: string; email: string }>;
}): Promise<void> {
  const ctx = await resolveSeedContext({ endpointOverride: args.endpoint, appCredsOverride: args.appCredsOverride, subscriptionId: args.subscriptionId });
  if (!ctx) return; // no GitHub App creds → no brains → nothing to seed
  const token = await (args.getFoundryTokenImpl ?? getFoundryToken)();
  const { hadIntake, block } = await findOnboardingProfile({ endpoint: ctx.endpoint, token, fetchImpl: args.fetchImpl });
  if (block) {
    const azIdentity = args.azIdentity ?? await (args.getSignedInUserIdentityImpl ?? getSignedInUserIdentity)();
    await applyProfileToBrains({ block, brainRepos: ctx.brainRepos, branch: "main", appCreds: ctx.appCreds,
      subscriptionId: ctx.subscriptionId, azIdentity, fetchImpl: args.fetchImpl });
    args.stdout(`${colors.success("✓")} Your advisors now know your company + how to reach you (seeded ${ctx.brainRepos.join(", ")}).\n`);
    return;
  }
  if (!hadIntake) return; // founder skipped the intake — nothing to wait for
  (args.spawnWatch ?? spawnDetachedSeedWatch)();
  args.stdout(
    `${colors.dim("ℹ Your advisors will pick up your company profile when you finish the intake (watching in the background).")}\n` +
    `  ${colors.hint("or run:")} m8t bootstrap seed-profile\n`,
  );
}
